// Slice 12 — Quality Management Console.
// Tabs: Recordings (transcribe + auto-score + manual eval) · Evaluations · Coaching · Calibration.
import { useEffect, useMemo, useState, Fragment } from "react";
import {
  Loader2, RefreshCw, Mic, FileText, Sparkles, GraduationCap, Scale,
  PlayCircle, Wand2, ClipboardCheck, Plus, X, Trash2, Crown,
  CheckCircle2, XCircle, MessageSquare,
  Download, Search, ArrowDownLeft, ArrowUpRight, RotateCw, MonitorPlay, Archive,
  ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { DISPLAY_TZ } from "@/lib/app-utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataPagination } from "@/components/ui/data-pagination";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const TABS = [
  { id: "recordings",  label: "Recordings",  icon: Mic },
  { id: "evaluations", label: "Evaluations", icon: ClipboardCheck },
  { id: "calibration", label: "Calibration", icon: Scale },
  { id: "coaching",    label: "Coaching",    icon: GraduationCap },
];

function fmtSec(s) {
  const n = Number(s || 0);
  return n >= 60 ? `${Math.floor(n / 60)}m ${Math.round(n % 60)}s` : `${Math.round(n)}s`;
}

function passBadge(pass) {
  return pass
    ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-200">PASS</Badge>
    : <Badge className="bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300 border-red-200">FAIL</Badge>;
}

function scoreColor(pct) {
  if (pct >= 80) return "text-emerald-600 dark:text-emerald-400";
  if (pct >= 60) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

export default function QualityConsolePage() {
  const [tab, setTab] = useState("recordings");
  const [overview, setOverview] = useState(null);

  async function loadOverview() {
    try { const r = await api.get("/qm/overview"); setOverview(r.data); } catch {}
  }
  useEffect(() => { loadOverview(); }, [tab]);

  return (
    <div className="px-4 sm:px-6 py-6 w-full" data-testid="qm-console">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary"/> Quality Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recordings · auto-score against scorecards · manual evaluations · calibration · coaching plans.
          </p>
        </div>
      </div>

      {/* Overview tiles */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="qm-tiles">
          {[
            { id: "recordings", label: "Recordings", v: overview.recordings },
            { id: "evals", label: "Evaluations",    v: overview.evaluations },
            { id: "avg", label: "Avg Score",        v: `${overview.avg_score_pct}%` },
            { id: "pass", label: "Pass Rate",       v: `${overview.pass_rate_pct}%` },
          ].map((t) => (
            <Card key={t.id} className="p-4" data-testid={`qm-tile-${t.id}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.label}</div>
              <div className="font-display font-extrabold text-2xl mt-1">{t.v}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-4" data-testid="qm-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`qm-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all
                ${tab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="w-4 h-4"/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "recordings"  && <RecordingsTab onChanged={loadOverview} />}
      {tab === "evaluations" && <EvaluationsTab />}
      {tab === "calibration" && <CalibrationTab />}
      {tab === "coaching"    && <CoachingTab onChanged={loadOverview} />}
    </div>
  );
}


/* ===================== RECORDINGS TAB (Call Recordings Dashboard) ===================== */
// Backend stores UTC; render dates/times in the operator timezone (IST default).
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: DISPLAY_TZ }); }
  catch { return (iso || "").slice(0, 10); }
}
function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-GB", { timeZone: DISPLAY_TZ, hour: "2-digit", minute: "2-digit" }); }
  catch { return (iso || "").slice(11, 16); }
}
function fmtDateTimeShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: DISPLAY_TZ, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return (iso || "").slice(0, 16).replace("T", " "); }
}

function TypeBadge({ rec }) {
  const t = rec.call_type || (rec.direction === "inbound" ? "Inbound" : "Outbound");
  const cls = {
    Inbound: "text-sky-600 dark:text-sky-400",
    Manual: "text-amber-600 dark:text-amber-400",
    Predictive: "text-violet-600 dark:text-violet-400",
    "Predictive dial": "text-violet-600 dark:text-violet-400",
    "Power dial": "text-violet-600 dark:text-violet-400",
    "Progressive dial": "text-violet-600 dark:text-violet-400",
    "Preview dial": "text-violet-600 dark:text-violet-400",
    Callback: "text-violet-600 dark:text-violet-400",
    Outbound: "text-emerald-600 dark:text-emerald-400",
    Transfer: "text-orange-600 dark:text-orange-400",
  }[t] || "text-muted-foreground";
  const inbound = rec.direction === "inbound" && t !== "Transfer";
  const Icon = t === "Transfer" ? ArrowRightLeft : inbound ? ArrowDownLeft : ArrowUpRight;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-medium ${cls}`} data-testid={`rec-type-${rec.id}`}>
      <Icon className="w-3 h-3" /> {t}
    </span>
  );
}

function RecordingsTab({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scorecards, setScorecards] = useState([]);
  const [meta, setMeta] = useState({ agents: [], campaigns: [] });
  const [scoreModal, setScoreModal] = useState(null);
  const [manualModal, setManualModal] = useState(null);
  const [player, setPlayer] = useState(null); // {id, url, label}
  const [loadingAudio, setLoadingAudio] = useState(null); // rec id being fetched
  const [screenMap, setScreenMap] = useState({}); // call_id -> {id, duration_seconds, agent_name}
  const [screenPlayer, setScreenPlayer] = useState(null); // {url, label}
  const [loadingScreen, setLoadingScreen] = useState(null); // screen rec id being fetched
  const TODAY = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD (local)
  const [filters, setFilters] = useState({
    search: "", agent: "all", campaign: "all", direction: "all",
    date_from: TODAY, date_to: TODAY, audio_only: false,
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const REC_PAGE_SIZE = 50;

  async function load(toPage = 1) {
    setBusy(true);
    try {
      const params = { page: toPage, page_size: REC_PAGE_SIZE };
      if (filters.search) params.search = filters.search;
      if (filters.agent !== "all") params.agent_name = filters.agent;
      if (filters.campaign !== "all") params.campaign_id = filters.campaign;
      if (filters.direction !== "all") params.direction = filters.direction;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = `${filters.date_to}T23:59:59`;
      if (filters.audio_only) params.has_audio = true;
      const [r1, r2, r3] = await Promise.all([
        api.get("/qm/recordings", { params }),
        api.get("/qm/scorecards"),
        api.get("/qm/recordings/meta"),
      ]);
      setRows(r1.data.rows || []);
      setTotal(r1.data.total || 0);
      setPages(r1.data.pages || 1);
      setPage(r1.data.page || toPage);
      setScorecards(r2.data.rows || []);
      setMeta(r3.data || { agents: [], campaigns: [] });

      // Link any screen recordings captured for these calls (QA: audio + screen).
      const callIds = [...new Set((r1.data.rows || [])
        .flatMap((x) => [x.call_id, x.cdr_id]).filter(Boolean))];
      if (callIds.length) {
        api.get("/admin/screen-recording/by-calls", { params: { call_ids: callIds.join(",") } })
          .then((sr) => setScreenMap(sr.data.map || {}))
          .catch(() => setScreenMap({}));
      } else setScreenMap({});
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  // NOTE: filters.agent_name maps to backend search/agent — backend filters
  // by agent_name through the `search` OR explicit param; we pass agent_name.
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  async function sync() {
    setSyncing(true);
    try {
      const r = await api.post("/qm/recordings/sync");
      toast.success(`Synced — ${r.data.created} new recording${r.data.created === 1 ? "" : "s"}`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSyncing(false); }
  }

  async function exportZip() {
    setExporting(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.agent !== "all") params.agent_name = filters.agent;
      if (filters.campaign !== "all") params.campaign_id = filters.campaign;
      if (filters.direction !== "all") params.direction = filters.direction;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = `${filters.date_to}T23:59:59`;
      const r = await api.get("/qm/recordings/export", { responseType: "blob", params });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recordings_${filters.date_from || "all"}_${filters.date_to || "all"}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      const inc = Number(r.headers?.["x-included-count"]);
      const tot = Number(r.headers?.["x-total-count"]);
      const noAudio = Number(r.headers?.["x-no-audio-count"]);
      if (tot && noAudio > 0) {
        toast.success(
          `Downloaded ${inc} of ${tot} recordings — ${noAudio} had no audio file on disk and were skipped.`,
          { duration: 7000 });
      } else {
        toast.success(`Download started${inc ? ` — ${inc} recording${inc === 1 ? "" : "s"}` : ""}`);
      }
    } catch (e) {
      toast.error(e?.response?.status === 404
        ? "No recordings with audio for the selected filters"
        : formatErr(e));
    } finally { setExporting(false); }
  }

  async function fetchAudio(rec, download) {
    setLoadingAudio(rec.id);
    try {
      const r = await api.get(`/qm/recordings/${rec.id}/audio`, {
        responseType: "blob", params: download ? { download: true } : {},
      });
      const url = URL.createObjectURL(r.data);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = rec.recording_filename || `${(rec.agent_name || "call").replace(/\s+/g, "_")}_${rec.peer || ""}_${fmtDate(rec.started_at)}.${rec.has_audio && r.data.type.includes("mpeg") ? "mp3" : "wav"}`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      } else {
        if (player?.url) URL.revokeObjectURL(player.url);
        setPlayer({ id: rec.id, url, label: `${rec.agent_name || "—"} · ${rec.peer || ""} · ${fmtDate(rec.started_at)} ${fmtTime(rec.started_at)}` });
      }
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No audio file for this call yet" : formatErr(e));
    } finally { setLoadingAudio(null); }
  }

  async function fetchScreen(sr, rec) {
    setLoadingScreen(sr.id);
    try {
      const r = await api.get(`/admin/screen-recording/recordings/${sr.id}/stream`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      if (screenPlayer?.url) URL.revokeObjectURL(screenPlayer.url);
      setScreenPlayer({
        url,
        label: `${rec.agent_name || sr.agent_name || "—"} · ${rec.peer || ""} · ${fmtDate(rec.started_at)} ${fmtTime(rec.started_at)}`,
      });
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No screen recording file" : formatErr(e));
    } finally { setLoadingScreen(null); }
  }

  async function transcribe(rec_id) {    try {
      const r = await api.post(`/qm/recordings/${rec_id}/transcribe`);
      toast.success(`Transcribed via ${r.data.engine}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function runAutoScore(rec_id, sc_id) {
    setScoreModal(null); setBusy(true);
    try {
      const r = await api.post(`/qm/recordings/${rec_id}/auto-score`, { scorecard_id: sc_id });
      toast.success(`Auto-scored: ${r.data.score_pct}% ${r.data.pass ? "PASS" : "FAIL"}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  function resetFilters() {
    setFilters({ search: "", agent: "all", campaign: "all", direction: "all", date_from: TODAY, date_to: TODAY, audio_only: false });
    setTimeout(load, 0);
  }

  return (
    <>
      {/* Filter bar */}
      <Card className="p-3 mb-3" data-testid="rec-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[11px]">Search phone / name</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                data-testid="rec-search"
                className="h-9 pl-8"
                placeholder="9876543210 or Sarah…"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Agent</Label>
            <Select value={filters.agent} onValueChange={(v) => setFilters({ ...filters, agent: v })}>
              <SelectTrigger data-testid="rec-filter-agent" className="h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                {meta.agents.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Campaign</Label>
            <Select value={filters.campaign} onValueChange={(v) => setFilters({ ...filters, campaign: v })}>
              <SelectTrigger data-testid="rec-filter-campaign" className="h-9 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campaigns</SelectItem>
                {meta.campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Direction</Label>
            <Select value={filters.direction} onValueChange={(v) => setFilters({ ...filters, direction: v })}>
              <SelectTrigger data-testid="rec-filter-direction" className="h-9 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">From</Label>
            <Input data-testid="rec-date-from" type="date" className="h-9 w-36" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px]">To</Label>
            <Input data-testid="rec-date-to" type="date" className="h-9 w-36" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Switch data-testid="rec-audio-only" checked={filters.audio_only} onCheckedChange={(v) => setFilters({ ...filters, audio_only: !!v })} />
            <Label className="text-[11px]">Has audio</Label>
          </div>
          <Button data-testid="rec-apply" onClick={() => load(1)} disabled={busy} className="h-9 gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Apply
          </Button>
          <Button data-testid="rec-reset" variant="outline" onClick={resetFilters} className="h-9">Reset</Button>
        </div>
      </Card>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">
          {total} call recording{total === 1 ? "" : "s"}
          <span className="text-xs text-muted-foreground/70">
            {" · "}{filters.date_from || filters.date_to
              ? `${filters.date_from || "…"} → ${filters.date_to || "…"}`
              : "all dates"} · connected calls only
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportZip} disabled={exporting} className="gap-1.5" data-testid="rec-export-zip">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />} Download ZIP
          </Button>
          <Button variant="outline" onClick={sync} disabled={syncing} className="gap-1.5" data-testid="rec-sync">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Sync from calls
          </Button>
          <Button variant="outline" onClick={() => load(page)} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </Button>
        </div>
      </div>

      {/* Recordings table — the audio player renders INLINE, expanded directly
          under the row being played, and the active row is highlighted so it's
          always obvious which recording is playing. */}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="qm-recordings-table">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Date</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Time</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Phone</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Agent</th>
              <th className="text-center px-3 py-2 font-semibold text-xs uppercase tracking-wider">Type</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Disposition</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider">Duration</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Campaign / Queue</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Recording file</th>
              <th className="text-center px-3 py-2 font-semibold text-xs uppercase tracking-wider">Screen</th>
              <th className="text-center px-3 py-2 font-semibold text-xs uppercase tracking-wider">Score</th>
              <th className="text-center px-3 py-2 font-semibold text-xs uppercase tracking-wider">Evals</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={13} className="px-3 py-8 text-center text-sm text-muted-foreground italic">
                No connected call recordings for this date range. Showing today by default — widen the date range above to see older calls.
              </td></tr>
            )}
            {rows.map((r) => (
              <Fragment key={r.id}>
              <tr className={`border-t border-border/60 hover:bg-muted/20 ${player?.id === r.id ? "bg-emerald-50/70 dark:bg-emerald-950/30" : ""}`} data-testid={`qm-rec-${r.id}`}>
                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.started_at) || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtTime(r.started_at) || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.peer || "—"}</td>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{r.agent_name || "—"}</td>
                <td className="px-3 py-2 text-center"><TypeBadge rec={r} /></td>
                <td className="px-3 py-2 text-xs">{r.disposition ? <Badge variant="outline" className="text-[10px]">{r.disposition}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-2 text-right font-mono whitespace-nowrap" title={r.recording_seconds ? "Recording length (matches the audio file)" : "Call duration"}>{fmtSec(r.recording_seconds || r.duration_seconds)}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  {r.campaign_name ? <span>{r.campaign_name}</span> : <span className="text-muted-foreground">—</span>}
                  {r.queue_name ? <div className="text-[10px] text-sky-600 dark:text-sky-400">⮕ {r.queue_name}</div> : null}
                </td>
                <td className="px-3 py-2 text-[10px] font-mono max-w-[220px]">
                  {r.recording_filename
                    ? <span className="block truncate text-muted-foreground" title={r.recording_filename} data-testid={`rec-filename-${r.id}`}>{r.recording_filename}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {(() => {
                    const sr = screenMap[r.call_id] || screenMap[r.cdr_id];
                    return sr ? (
                      <Button size="icon" variant="ghost" title={`Play screen recording (${fmtSec(sr.duration_seconds)})`}
                        disabled={loadingScreen === sr.id}
                        onClick={() => fetchScreen(sr, r)} className="h-7 w-7" data-testid={`rec-screen-${r.id}`}>
                        {loadingScreen === sr.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <MonitorPlay className="w-4 h-4 text-violet-600" />}
                      </Button>
                    ) : <span className="text-muted-foreground/40" title="No screen recording for this call">—</span>;
                  })()}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.score_pct != null ? (
                    <Badge variant="outline" className={`text-[10px] font-mono ${r.score_pass === false ? "border-rose-400 text-rose-600 dark:text-rose-400" : "border-emerald-400 text-emerald-600 dark:text-emerald-400"}`} data-testid={`rec-score-${r.id}`}>
                      {Math.round(r.score_pct)}%
                    </Badge>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-center font-mono">{r.eval_count ?? 0}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" title={r.has_audio ? "Play" : "No audio file yet"} disabled={!r.has_audio || loadingAudio === r.id}
                    onClick={() => fetchAudio(r, false)} className="h-7 w-7" data-testid={`rec-play-${r.id}`}>
                    {loadingAudio === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className={`w-4 h-4 ${r.has_audio ? "text-emerald-600" : "text-muted-foreground/40"}`} />}
                  </Button>
                  <Button size="icon" variant="ghost" title={r.has_audio ? "Download" : "No audio file yet"} disabled={!r.has_audio}
                    onClick={() => fetchAudio(r, true)} className="h-7 w-7" data-testid={`rec-download-${r.id}`}>
                    <Download className={`w-4 h-4 ${r.has_audio ? "" : "text-muted-foreground/40"}`} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => transcribe(r.id)} className="h-7 ml-1 mr-1 text-xs gap-1" data-testid={`qm-transcribe-${r.id}`}>
                    <FileText className="w-3 h-3" /> {r.transcript_status === "ready" ? "Re-tx" : "Transcribe"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setScoreModal({ rec: r })} className="h-7 mr-1 text-xs gap-1" data-testid={`qm-auto-${r.id}`}>
                    <Wand2 className="w-3 h-3" /> Score
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setManualModal({ rec: r })} className="h-7 text-xs gap-1" data-testid={`qm-manual-${r.id}`}>
                    <ClipboardCheck className="w-3 h-3" /> Manual
                  </Button>
                </td>
              </tr>
              {player?.id === r.id && (
                <tr className="bg-emerald-50/60 dark:bg-emerald-950/20 border-t border-emerald-300/50" data-testid="rec-player">
                  <td colSpan={13} className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <PlayCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-xs font-medium truncate max-w-[260px]" title={player.label}>Now playing · {player.label}</span>
                      <audio key={player.id} src={player.url} controls autoPlay className="h-9 flex-1" data-testid="rec-audio-el" />
                      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => { URL.revokeObjectURL(player.url); setPlayer(null); }} data-testid="rec-player-close">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
        <DataPagination page={page} pages={pages} total={total} pageSize={REC_PAGE_SIZE} onPage={(p) => load(p)} testId="qm-rec-pagination" />
      </Card>

      {/* Auto-score modal — pick a scorecard */}
      <Dialog open={!!scoreModal} onOpenChange={(o) => !o && setScoreModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Auto-score recording</DialogTitle>
          </DialogHeader>
          {scoreModal && (
            <div className="space-y-3">
              <div className="text-sm">
                Agent: <span className="font-medium">{scoreModal.rec.agent_name}</span> ·
                Duration: <span className="font-mono">{fmtSec(scoreModal.rec.duration_seconds)}</span>
              </div>
              <div>
                <Label>Scorecard</Label>
                <Select onValueChange={(v) => setScoreModal({ ...scoreModal, sc: v })}>
                  <SelectTrigger data-testid="qm-score-sc-pick"><SelectValue placeholder="Pick a scorecard…"/></SelectTrigger>
                  <SelectContent>
                    {scorecards.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} (pass {s.pass_threshold}%)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScoreModal(null)}>Cancel</Button>
            <Button
              data-testid="qm-score-run"
              disabled={!scoreModal?.sc || busy}
              onClick={() => runAutoScore(scoreModal.rec.id, scoreModal.sc)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
              Run with active LLM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {manualModal && (
        <ManualEvalDialog
          recording={manualModal.rec}
          scorecards={scorecards}
          onClose={() => setManualModal(null)}
          onSaved={() => { setManualModal(null); load(); onChanged?.(); }}
        />
      )}

      {/* Linked screen recording player */}
      <Dialog open={!!screenPlayer} onOpenChange={(o) => {
        if (!o) { if (screenPlayer?.url) URL.revokeObjectURL(screenPlayer.url); setScreenPlayer(null); }
      }}>
        <DialogContent className="max-w-3xl" data-testid="rec-screen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MonitorPlay className="w-4 h-4 text-violet-600" /> Screen recording · {screenPlayer?.label}
            </DialogTitle>
          </DialogHeader>
          {screenPlayer?.url && (
            <video src={screenPlayer.url} controls autoPlay
              className="w-full rounded-lg bg-black max-h-[70vh]" data-testid="rec-screen-video" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


/* ===================== MANUAL EVAL DIALOG ===================== */
function ManualEvalDialog({ recording, scorecards, onClose, onSaved }) {
  const [scId, setScId] = useState(scorecards[0]?.id || "");
  const [scDetail, setScDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!scId) return;
    api.get(`/qm/scorecards/${scId}`)
      .then((r) => { setScDetail(r.data); setAnswers({}); })
      .catch((e) => toast.error(formatErr(e)));
  }, [scId]);

  const total = useMemo(() => {
    if (!scDetail) return { earned: 0, max: 0 };
    let e = 0, m = 0;
    for (const s of scDetail.sections) {
      for (const q of s.questions) {
        const key = `${s.title}::${q.q}`;
        const pct = Number(answers[key] ?? 0);
        m += Number(q.weight || 0);
        e += Number(q.weight || 0) * pct / 100;
      }
    }
    return { earned: Math.round(e * 10) / 10, max: m };
  }, [scDetail, answers]);

  async function save() {
    if (!scDetail) return;
    setBusy(true);
    try {
      const payload = {
        recording_id: recording.id, scorecard_id: scDetail.id,
        comments,
        answers: scDetail.sections.flatMap((s) =>
          s.questions.map((q) => ({
            section: s.title, q: q.q,
            score_pct: Number(answers[`${s.title}::${q.q}`] ?? 0),
          }))
        ),
      };
      await api.post("/qm/evaluations", payload);
      toast.success(`Saved — ${total.earned}/${total.max} pts`);
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="qm-manual-dialog">
        <DialogHeader>
          <DialogTitle>Manual evaluation · {recording.agent_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Scorecard</Label>
            <Select value={scId} onValueChange={setScId}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                {scorecards.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {scDetail?.sections.map((s) => (
            <div key={s.title} className="rounded-md border border-border p-3">
              <div className="font-display font-bold text-sm mb-2">{s.title}</div>
              <div className="space-y-2">
                {s.questions.map((q) => {
                  const key = `${s.title}::${q.q}`;
                  const val = answers[key] ?? "";
                  return (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <span className="flex-1 min-w-0">{q.q}</span>
                      <span className="text-xs text-muted-foreground shrink-0">w={q.weight}</span>
                      <Input
                        type="number" min={0} max={100}
                        placeholder="0"
                        className="w-20 h-7 text-xs font-mono"
                        value={val}
                        onChange={(e) => setAnswers({ ...answers, [key]: e.target.value })}
                        data-testid={`qm-manual-q-${q.q.slice(0, 20)}`}
                      />
                      <span className="text-xs text-muted-foreground w-4">%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div>
            <Label>Comments</Label>
            <Textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)}
              placeholder="Coaching notes for the agent…"/>
          </div>
          <div className="flex items-center justify-between p-3 rounded-md bg-muted/40">
            <span className="text-sm font-medium">Total</span>
            <span className={`font-display font-extrabold text-2xl ${scoreColor(total.max ? total.earned / total.max * 100 : 0)}`}>
              {total.earned} / {total.max}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !scDetail} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="qm-manual-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
            Save evaluation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ===================== EVALUATIONS TAB ===================== */
function EvaluationsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/qm/evaluations?limit=200").then((r) => setRows(r.data.rows || [])); }, []);

  return (
    <Card className="overflow-hidden" data-testid="qm-evals-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">When</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Agent</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Scorecard</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Evaluator</th>
            <th className="text-right px-3 py-2 text-xs uppercase tracking-wider">Score</th>
            <th className="text-center px-3 py-2 text-xs uppercase tracking-wider">Pass</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground italic">No evaluations yet.</td></tr>
          )}
          {rows.map((e) => (
            <tr key={e.id} className="border-t border-border/60 hover:bg-muted/20 cursor-pointer" onClick={() => setOpen(e)} data-testid={`qm-eval-row-${e.id}`}>
              <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDateTimeShort(e.created_at)}</td>
              <td className="px-3 py-2 font-medium">{e.agent_name || "—"}</td>
              <td className="px-3 py-2 text-xs">{e.scorecard_name}</td>
              <td className="px-3 py-2 text-xs">
                {e.evaluator_name} <Badge variant="outline" className="text-[9px] ml-1">{e.kind}</Badge>
              </td>
              <td className={`px-3 py-2 text-right font-display font-extrabold ${scoreColor(e.score_pct)}`}>{e.score_pct}%</td>
              <td className="px-3 py-2 text-center">{passBadge(e.pass)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Evaluation detail</DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline">{open.kind}</Badge>
                <Badge variant="outline">{open.scorecard_name}</Badge>
                {open.model && <Badge variant="outline">{open.model}</Badge>}
                {open.is_mocked && <Badge variant="outline" className="bg-amber-50">heuristic</Badge>}
                {passBadge(open.pass)}
                <span className={`ml-auto font-display font-extrabold text-2xl ${scoreColor(open.score_pct)}`}>{open.score_pct}%</span>
              </div>
              {open.summary && <p className="text-sm italic text-muted-foreground border-l-2 border-primary/30 pl-3">{open.summary}</p>}
              <div className="space-y-2">
                {open.section_scores?.map((s) => (
                  <div key={s.title} className="rounded-md bg-muted/30 p-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{s.title}</span>
                      <span className={`font-mono ${scoreColor(s.score_pct)}`}>{s.earned}/{s.max} ({s.score_pct}%)</span>
                    </div>
                  </div>
                ))}
              </div>
              {open.answers?.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Show per-question breakdown ({open.answers.length})</summary>
                  <div className="mt-2 space-y-1.5">
                    {open.answers.map((a, i) => (
                      <div key={i} className="rounded-md border border-border/60 p-2">
                        <div className="flex justify-between text-xs gap-2">
                          <span className="font-medium flex-1 min-w-0">{a.section} · {a.q}</span>
                          <span className={`font-mono shrink-0 ${scoreColor(a.score_pct)}`}>{a.score_pct}%</span>
                        </div>
                        {a.reasoning && <div className="text-[11px] text-muted-foreground mt-1 italic">{a.reasoning}</div>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {open.comments && <p className="text-sm border-l-2 border-secondary/40 pl-3 italic">{open.comments}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/* ===================== CALIBRATION TAB ===================== */
function CalibrationTab() {
  const [recs, setRecs] = useState([]);
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/qm/recordings").then((r) => setRecs(r.data.rows || []));
  }, []);
  useEffect(() => {
    if (!active) return;
    api.get(`/qm/recordings/${active}/calibration`).then((r) => setData(r.data));
  }, [active]);

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4">
      <Card className="p-3" data-testid="qm-calib-list">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 px-2">Pick a recording</div>
        <ul className="space-y-1 max-h-[600px] overflow-y-auto">
          {recs.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => setActive(r.id)}
                className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors
                  ${active === r.id ? "bg-primary/10 border-l-2 border-primary" : "hover:bg-muted/40"}`}
              >
                <div className="font-medium truncate">{r.agent_name}</div>
                <div className="text-[10px] text-muted-foreground">
                  {r.eval_count || 0} evaluator{(r.eval_count || 0) === 1 ? "" : "s"} · {fmtSec(r.duration_seconds)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-5" data-testid="qm-calib-detail">
        {!active && <p className="text-sm text-muted-foreground italic">Pick a recording on the left to see cross-evaluator variance.</p>}
        {active && !data && <Loader2 className="w-5 h-5 animate-spin"/>}
        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Card className="p-3">
                <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Evaluators</div>
                <div className="font-display font-extrabold text-2xl mt-1">{data.n_evaluators}</div>
              </Card>
              <Card className="p-3">
                <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Mean</div>
                <div className={`font-display font-extrabold text-2xl mt-1 ${scoreColor(data.mean_pct || 0)}`}>{data.mean_pct ?? "—"}%</div>
              </Card>
              <Card className="p-3">
                <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Std Dev</div>
                <div className="font-display font-extrabold text-2xl mt-1">{data.stddev_pct ?? 0}</div>
              </Card>
              <Card className="p-3">
                <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Range</div>
                <div className="font-display font-extrabold text-2xl mt-1">{data.min_pct ?? "—"} – {data.max_pct ?? "—"}</div>
              </Card>
            </div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Evaluators</h4>
            <div className="space-y-1.5 mb-4">
              {data.evaluators.map((e) => (
                <div key={e.id + e.created_at} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="text-[10px]">{e.kind}</Badge>
                  <span className="flex-1 min-w-0 truncate">{e.name}</span>
                  <span className={`font-mono font-bold ${scoreColor(e.score_pct)}`}>{e.score_pct}%</span>
                </div>
              ))}
            </div>
            {data.section_variances?.length > 0 && (
              <>
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Per-section variance</h4>
                <div className="rounded-md border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr><th className="text-left px-3 py-1.5 text-xs">Section</th><th className="text-right px-3 py-1.5 text-xs">Mean</th><th className="text-right px-3 py-1.5 text-xs">Std Dev</th><th className="text-right px-3 py-1.5 text-xs">N</th></tr>
                    </thead>
                    <tbody>
                      {data.section_variances.map((sv) => (
                        <tr key={sv.title} className="border-t border-border/60">
                          <td className="px-3 py-1.5">{sv.title}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{sv.mean}%</td>
                          <td className="px-3 py-1.5 text-right font-mono">{sv.stddev}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{sv.n}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}


/* ===================== COACHING TAB ===================== */
function CoachingTab({ onChanged }) {
  const [plans, setPlans] = useState([]);
  const [agents, setAgents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(null); // plan_id
  const [form, setForm] = useState({ agent_id: "", agent_name: "", focus_areas: "" });

  async function load() {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/qm/coaching"),
        api.get("/admin/users").catch(() => ({ data: { rows: [] } })),
      ]);
      setPlans(r1.data.rows || []);
      setAgents(r2.data.rows || r2.data.users || []);
    } catch (e) { toast.error(formatErr(e)); }
  }
  useEffect(() => { load(); }, []);

  async function createPlan() {
    if (!form.agent_id) { toast.error("Pick an agent"); return; }
    try {
      await api.post("/qm/coaching", {
        agent_id: form.agent_id,
        agent_name: form.agent_name,
        focus_areas: form.focus_areas.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Coaching plan created");
      setCreating(false); setForm({ agent_id: "", agent_name: "", focus_areas: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function closePlan(id) {
    if (!window.confirm("Close this coaching plan?")) return;
    try { await api.post(`/qm/coaching/${id}/close`); load(); onChanged?.(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">{plans.length} plans</div>
        <Button onClick={() => setCreating(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="qm-coach-new">
          <Plus className="w-4 h-4"/> New coaching plan
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {plans.length === 0 && (
          <p className="text-sm text-muted-foreground italic col-span-full">No coaching plans yet.</p>
        )}
        {plans.map((p) => (
          <Card key={p.id} className="p-4" data-testid={`qm-plan-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold truncate">{p.agent_name || p.agent_id}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Opened {fmtDate(p.created_at)} · {p.sessions?.length || 0} sessions</div>
              </div>
              <Badge variant="outline" className={p.status === "open" ? "bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-muted"}>
                {p.status}
              </Badge>
            </div>
            {p.focus_areas?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {p.focus_areas.map((f) => <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>)}
              </div>
            )}
            {p.sessions?.length > 0 && (
              <ul className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                {p.sessions.map((s) => (
                  <li key={s.id} className="text-xs text-muted-foreground border-l-2 border-secondary/40 pl-2">
                    <span className="font-medium text-foreground">{s.author_name}</span> · {fmtDate(s.created_at)} — {s.note}
                  </li>
                ))}
              </ul>
            )}
            {p.status === "open" && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setSessionOpen(p.id)} className="h-7 text-xs flex-1 gap-1" data-testid={`qm-plan-add-${p.id}`}>
                  <MessageSquare className="w-3 h-3"/> Add session
                </Button>
                <Button size="sm" variant="outline" onClick={() => closePlan(p.id)} className="h-7 text-xs gap-1" data-testid={`qm-plan-close-${p.id}`}>
                  <XCircle className="w-3 h-3"/> Close
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Create plan dialog */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>New coaching plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Agent</Label>
              <Select value={form.agent_id} onValueChange={(v) => {
                const a = agents.find((x) => x.id === v);
                setForm({ ...form, agent_id: v, agent_name: a?.name || a?.email || "" });
              }}>
                <SelectTrigger data-testid="qm-coach-agent"><SelectValue placeholder="Pick an agent…"/></SelectTrigger>
                <SelectContent>
                  {agents.filter((a) => a.role === "agent" || a.role === "supervisor").map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Focus areas (comma-separated)</Label>
              <Input
                data-testid="qm-coach-focus"
                value={form.focus_areas}
                onChange={(e) => setForm({ ...form, focus_areas: e.target.value })}
                placeholder="Opening, Discovery questions, Compliance"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createPlan} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="qm-coach-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add session dialog */}
      {sessionOpen && (
        <AddSessionDialog
          planId={sessionOpen}
          onClose={() => setSessionOpen(null)}
          onSaved={() => { setSessionOpen(null); load(); }}
        />
      )}
    </>
  );
}

function AddSessionDialog({ planId, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [items, setItems] = useState("");
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/qm/coaching/${planId}/sessions`, {
        note,
        action_items: items.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Session added");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add coaching session</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Session note</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="qm-session-note"/>
          </div>
          <div>
            <Label>Action items (one per line)</Label>
            <Textarea rows={3} value={items} onChange={(e) => setItems(e.target.value)} placeholder="Practice 5 opening calls&#10;Review recording #abc" data-testid="qm-session-items"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !note.trim()} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="qm-session-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
